<!--suppress UnnecessaryLabelJS -->
<script lang="ts">
    import '$lib/styles/styles.css';
    import {hiddenFn} from '$lib/utils/hiddenFn.js';
    import {beforeNavigate} from '$app/navigation';
    import {page, updated} from '$app/state';
    import RemoteChanges from '$lib/components/_page/RemoteChanges.svelte';
    import {logInProduction} from '$lib/utils/logInProduction.js';
    import {browser, version} from '$app/environment';
    import {setContext} from 'svelte';
    import Toasts from '$lib/components/_page/toasts.svelte';
    import {protectRoute} from '$lib/helper-fns/protect-route';
    import {
        keyUserActionProcessor,
        keyAuth,
        keyDevice,
        keyFunctions,
        setToastManagerContext,
        keyLoggedIn,
        keyIsNativePlatform,
        keyPlatform,
        keySentenceManager,
        keyAudioPlayer,
        keyVerbConjugations,
        keyUserWordManager,
    } from '$lib/context-keys';
    import AppError from '$lib/components/_page/app-error.svelte';
    import {ConnectionStatusState} from '$lib/classes/state/connection-status.svelte.js';
    import Offline from '$lib/components/_page/offline.svelte';

    let {
        data,
        children,
    } = $props()

    const {
        isNativePlatform,
        platform,
        appError,
        connectionStatus,
        setup,
        sentenceManager,
        userSettings,
        loggedIn,
        remoteChanges,
        device,
        functions,
        auth,
        toastManager,
        userWordManager,
        userActionProcessor,
        audioPlayer,
        verbConjugations,
    } = data

    setContext(keyIsNativePlatform, isNativePlatform)
    setContext(keyPlatform, platform)
    setContext(keyLoggedIn, loggedIn)
    setContext(keyDevice, device)
    setContext(keyFunctions, functions)
    setContext(keyAuth, auth)
    setToastManagerContext(toastManager)
    setContext(keyUserWordManager, userWordManager)
    setContext(keyUserActionProcessor, userActionProcessor)
    setContext(keySentenceManager, sentenceManager)
    setContext(keyAudioPlayer, audioPlayer)
    setContext(keyVerbConjugations, verbConjugations)

    function handleKeydown(event: KeyboardEvent) {
        if (event.code === 'KeyH' && event.altKey && event.metaKey) hiddenFn()
    }

    // VERSION UPDATE
    beforeNavigate(async navigation => {
        await updated.check()
        // NOTE $updated doesn't seem to be checked in dev mode !!!
        // NOTE $updated needs to be used with $ syntax so there's a subscriber to the store (what if no subscriber..?!)
        // NOTE navigation.to might be null - I think that's the case after window.location.href is set and callback runs once more
        if (updated.current) {
            logInProduction(`$updated >>> navigation\n ${JSON.stringify(navigation, null, 2)}`)
        }
        if (updated.current && navigation.to) {
            logInProduction(`beforeNavigate - ${version} - $updated && navigation.to `)
            const navigatingTo = navigation.to.url.href
            navigation.cancel()
            logInProduction('navigating to ' + navigatingTo)
            window.location.href = navigatingTo
        }
    })

    // PROTECT ROUTES
    beforeNavigate(async (navigation) => {
        await setup
        if (navigation.to) {
            await protectRoute(page.url, navigation.to.url, loggedIn.current)
        }
    })

    async function checkAndHandleVersionUpdate() {
        await updated.check()
        if (updated.current) {
            logInProduction(`window onfocus - version ${version} - updated! >> reload`)
            window.location.reload();
        }
    }

    const dev = import.meta.env.DEV
    const prod = import.meta.env.PROD
</script>

<svelte:head>
    {#if dev}
        <!-- prevent staging from being indexed by (google) bot -->
        <meta name="robots" content="noindex">
    {/if}

    {#if browser && platform === 'web'}
        <!-- NOTE Tawk yOffset stopped working with initialization in hooks.client.ts -->
        {@const allScriptNotDisabled = localStorage.getItem('disableScripts') === null}
        {@const tawkNotDisabled = localStorage.getItem('disableTawk') === null}
        {@const tawkEnabled = localStorage.getItem('enableTawk') !== null}
        {#if tawkEnabled || (prod && allScriptNotDisabled && tawkNotDisabled)}
            <script src="/tawk.js"></script>
        {/if}
    {/if}
</svelte:head>

<svelte:window
        onkeydown={handleKeydown}
        onfocus={checkAndHandleVersionUpdate}
/>

{#if connectionStatus.current === ConnectionStatusState.Offline}

    <Offline/>

{:else if appError.current}

    <AppError/>

{:else if remoteChanges.current}

    <RemoteChanges/>

{:else}

    <Toasts toastManager={toastManager}/>

    <!--{#await setup then _}-->
    <!--    {#if loggedIn.current && (auth.getDaysSignedUp() ?? 0) >= 7}-->
    <!--        <PopupModal-->
    <!--                localStorageKey="survey-one"-->
    <!--                maxWidth={500}-->
    <!--                background="var(&#45;&#45;color-light)"-->
    <!--                let:closeDialog-->
    <!--        >-->
    <!--            <NextFeature-->
    <!--                    auth={auth}-->
    <!--                    database={database}-->
    <!--                    isPayingCustomer={isPayingCustomer.current}-->
    <!--                    skippeable={true}-->
    <!--                    on:skip={closeDialog}-->
    <!--                    closeable={true}-->
    <!--                    on:close={closeDialog}-->
    <!--            />-->
    <!--        </PopupModal>-->
    <!--    {/if}-->
    <!--{/await}-->

    <div style="height: 100%;"
         style:--selected-color={userSettings.get('selectedColor')}
         style:--color-maskulinum={userSettings.get('nounColors').maskulinum}
         style:--color-femininum={userSettings.get('nounColors').femininum}
         style:--color-neutrum={userSettings.get('nounColors').neutrum}
    >
        {@render children()}
    </div>
{/if}
